import React, { type FC, useCallback } from 'react';

import times from 'lodash/times';

import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Stack } from '@atlaskit/primitives/compiled';
import {
	CardsOrientation,
	type ItemDimensions,
	ItemSelectedToEditWrapper,
	LoadingCardsError,
	RenderVariant,
	useAudienceSegmentation,
	type useAudienceSegmentationProps,
	useItemsCombinedExperienceState,
	useProductDependencies,
} from '@atlassian/company-hub-common';

import { useCards } from '../../../controllers/use-cards';
import {
	useValidateRequiredCardsParameters,
	type ValidatedLinkCardsParameters,
} from '../../../controllers/validators';
import {
	type Alignment,
	type Card,
	type CardsExtensionParameters,
	CardSize,
	CardsLayout,
	Orientation,
} from '../../../types';

import { AutoscrollCards } from './autoscroll-cards';
import {
	getCardDimensions,
	LargeCardV1,
	LargeCardV2,
	MediumCardV1,
	MediumCardV2,
	SmallCardV1,
} from './card-size';
import { LargeCard } from './card-size/large-card/large-card-v0';
import { LargePlaceholderCard } from './card-size/large-card/large-placeholder-card';
import { MediumCard } from './card-size/medium-card/medium-card-v0';
import { MediumPlaceholderCard } from './card-size/medium-card/medium-placeholder-card';
import { SmallCard } from './card-size/small-card/small-card-v0';
import { SmallPlaceholderCard } from './card-size/small-card/small-placeholder-card';
import { DynamicCardEmptyState, EmptyState } from './dynamic-card-empty-state';
import { DynamicCardLoadingState } from './dynamic-card-loading-state';
import { EmptyCard } from './empty-card';
import { CardsContentFooter } from './orientation/footer';
import type { CommonCardProps } from './types';
import { ZeroResultsCard } from './zero-results-card';

const styles = cssMap({
	cardWrapper: {
		height: '100%',
	},
});

export type LinkCardsContentProps = {
	contentId: string;
	extensionLocalId: string;
	isInViewMode?: boolean;
	parameters: CardsExtensionParameters;
} & Pick<useAudienceSegmentationProps, 'isTargetAudience' | 'isCompanyHub'>;

type CardsContentProps = LinkCardsContentProps &
	Pick<CommonCardProps, 'cardDimensions' | 'showDescription'> & {
		CardComponent: FC<CommonCardProps>;
		parameters: ValidatedLinkCardsParameters;
		shouldAutoscrollCards: boolean;
	};

const CARD_SIZE_V1_MAP = {
	[CardSize.SMALL]: SmallCardV1,
	[CardSize.MEDIUM]: MediumCardV1,
	[CardSize.LARGE]: LargeCardV1,
};

const CARD_SIZE_V2_MAP = {
	[CardSize.SMALL]: SmallCardV1,
	[CardSize.MEDIUM]: MediumCardV2,
	[CardSize.LARGE]: LargeCardV2,
};

const CARD_SIZE_MAP = {
	[CardSize.SMALL]: SmallCard,
	[CardSize.MEDIUM]: MediumCard,
	[CardSize.LARGE]: LargeCard,
};

const getCardComponent = (cardSize: CardSize, layout: CardsExtensionParameters['layout']) => {
	if (fg('company_hub_cards_v2')) {
		if (layout === CardsLayout.FIXED_IMAGE_HEIGHT) {
			return CARD_SIZE_V2_MAP[cardSize] ?? MediumCardV2;
		} else if (layout === CardsLayout.FLEXIBLE_IMAGE_HEIGHT) {
			return CARD_SIZE_V1_MAP[cardSize] ?? MediumCardV1;
		}
	}
	if (fg('company_hub_cards_visual_update')) {
		return CARD_SIZE_V1_MAP[cardSize] ?? MediumCardV1;
	}
	return CARD_SIZE_MAP[cardSize] ?? MediumCard;
};

export const LinkCardsContent = ({
	extensionLocalId,
	parameters,
	isCompanyHub,
	isTargetAudience,
	isInViewMode,
	contentId,
}: LinkCardsContentProps): React.JSX.Element => {
	const validatedCardParams = useValidateRequiredCardsParameters(parameters);
	const { renderVariant, size, isDescriptionShown, layout } = validatedCardParams;

	const isDynamicCards = renderVariant === RenderVariant.DYNAMIC;
	const cardDimensions = getCardDimensions({
		cardSize: size,
		layout,
		isCardsVisualUpdateEnabled: fg('company_hub_cards_visual_update'),
		isCardsV2Enabled: fg('company_hub_cards_v2'),
	});

	const CardComponent = getCardComponent(size, layout);
	// checking specifically if isDescriptionShown is false because if it's undefined,
	// it means that the dynamic cards were created from before and we want to show the descriptions they had
	const showDescription =
		!fg('company_hub_dynamic_cards_ai_descriptions') ||
		renderVariant === RenderVariant.MANUAL ||
		isDescriptionShown !== false;

	/* only autoscroll cards when all below conditions meet:
	 * 1. is in company hub
	 * 2. orientation is in row (handled in validateCardParams)
	 * 3. Autoscroll toggle is on
	 * 4. user is in renderer
	 * cards macro now includes features that are company hub specific. These features will be hidden for macros in non-hub pages
	 * for more details refer to https://hello.atlassian.net/wiki/spaces/CLI/pages/4436003008/Hub+only+features
	 */

	const shouldAutoscrollCards = Boolean(
		isCompanyHub && validatedCardParams.isAutoscrollEnabled && isInViewMode,
	);
	const CardsContentComponent = isDynamicCards ? DynamicCardsContent : ManualCardsContent;

	return (
		<CardsContentComponent
			extensionLocalId={extensionLocalId}
			parameters={validatedCardParams}
			isCompanyHub={isCompanyHub}
			isTargetAudience={isTargetAudience}
			isInViewMode={isInViewMode}
			contentId={contentId}
			cardDimensions={cardDimensions}
			shouldAutoscrollCards={shouldAutoscrollCards}
			CardComponent={CardComponent}
			showDescription={showDescription}
		/>
	);
};

type CardsContainerProps = {
	alignment: Alignment;
	cardDimensions: ItemDimensions;
	cards: Card[];
	extensionLocalId: string;
	isInViewMode: boolean;
	isStacked: boolean;
	numberOfCards: number;
	renderCard: (card: Card) => React.ReactElement;
	shouldAutoscrollCards: boolean;
	showAIDisclaimer?: boolean;
};

const CardsContainer = ({
	extensionLocalId,
	numberOfCards,
	cardDimensions,
	isStacked,
	alignment,
	cards,
	renderCard,
	isInViewMode,
	shouldAutoscrollCards,
	showAIDisclaimer,
}: CardsContainerProps) =>
	shouldAutoscrollCards ? (
		<AutoscrollCards
			numberOfCards={numberOfCards}
			cardDimensions={cardDimensions}
			cards={cards}
			renderCard={renderCard}
			showAIDisclaimer={showAIDisclaimer}
		/>
	) : (
		<CardsOrientation<Card>
			extensionLocalId={extensionLocalId}
			numberOfCards={numberOfCards}
			cardDimensions={cardDimensions}
			isStacked={isStacked}
			isInViewMode={isInViewMode}
			alignment={alignment}
			cards={cards}
			renderCard={renderCard}
			showAIDisclaimer={showAIDisclaimer}
			cardsContentFooter={CardsContentFooter}
		/>
	);

const ManualCardsContent = ({
	extensionLocalId,
	parameters,
	contentId,
	cardDimensions,
	CardComponent,
	shouldAutoscrollCards,
	isCompanyHub,
	isTargetAudience,
	isInViewMode = false,
	showDescription = true,
}: CardsContentProps) => {
	const {
		size,
		isPaddingEnabled,
		isAvatarShown,
		isPublishDateShown,
		isSocialStatsShown,
		isIconShown,
		cards,
		isSegmentationShown,
		isLabelShown,
		orientation,
		alignment,
		audienceSegmentationGroupIds,
		segmentationTarget,
		renderVariant,
		backgroundColor,
		textColor,
		borderColor,
		cornerStyle,
	} = parameters;
	const { areAllItemsLoaded, itemFailureError, onItemSucceeded, onItemFailed } =
		useItemsCombinedExperienceState(cards);
	const { filteredCards } = useAudienceSegmentation({
		isCompanyHub,
		isSegmentationShown,
		isTargetAudience,
		cards,
		isInViewMode,
		segmentationTarget,
		audienceSegmentationGroupIds,
		renderVariant,
	});

	const { experienceSuccessComponent, experienceStopComponent } = useProductDependencies();
	const ExperienceSuccess = experienceSuccessComponent || (() => null);
	const ExperienceError = experienceStopComponent || (() => null);

	const numberOfCards = filteredCards.length;

	const renderManualCard = useCallback(
		(card: Card) => {
			return (
				<Stack space="space.075" key={card.cardId} xcss={cx(styles.cardWrapper)}>
					{fg('company_hub_cards_visual_update') ? (
						<CardComponent
							contentId={contentId}
							cardDimensions={cardDimensions}
							isPaddingEnabled={isPaddingEnabled}
							isAvatarShown={isAvatarShown}
							isPublishDateShown={isPublishDateShown}
							isSocialStatsShown={isSocialStatsShown}
							isIconShown={isIconShown}
							isInViewMode={isInViewMode}
							isLabelShown={isLabelShown}
							onCardSucceeded={onItemSucceeded}
							onCardFailed={onItemFailed}
							isManualVariant
							extensionLocalId={extensionLocalId}
							backgroundColor={backgroundColor}
							textColor={textColor}
							borderColor={borderColor}
							cornerStyle={cornerStyle}
							showDescription={showDescription}
							{...card}
						/>
					) : (
						<ItemSelectedToEditWrapper
							key={card.cardId}
							itemId={card.cardId}
							extensionLocalId={extensionLocalId}
						>
							{!!card.link ? (
								<CardComponent
									contentId={contentId}
									cardDimensions={cardDimensions}
									isAvatarShown={isAvatarShown}
									isPublishDateShown={isPublishDateShown}
									isInViewMode={isInViewMode}
									onCardSucceeded={onItemSucceeded}
									onCardFailed={onItemFailed}
									showDescription={showDescription}
									{...card}
								/>
							) : (
								<EmptyCard
									contentId={contentId}
									size={size}
									cardDimensions={cardDimensions}
									isInViewMode={isInViewMode}
									onCardSucceeded={onItemSucceeded}
									onCardFailed={onItemFailed}
									{...card}
								/>
							)}
						</ItemSelectedToEditWrapper>
					)}
				</Stack>
			);
		},
		[
			extensionLocalId,
			contentId,
			cardDimensions,
			isPaddingEnabled,
			isAvatarShown,
			isPublishDateShown,
			isLabelShown,
			isSocialStatsShown,
			isIconShown,
			showDescription,
			size,
			isInViewMode,
			onItemSucceeded,
			onItemFailed,
			CardComponent,
			backgroundColor,
			textColor,
			borderColor,
			cornerStyle,
		],
	);

	return (
		<>
			<CardsContainer
				shouldAutoscrollCards={shouldAutoscrollCards}
				numberOfCards={numberOfCards}
				cardDimensions={cardDimensions}
				isStacked={orientation === Orientation.STACKED}
				isInViewMode={isInViewMode}
				alignment={alignment}
				extensionLocalId={extensionLocalId}
				cards={filteredCards}
				renderCard={renderManualCard}
			/>
			{areAllItemsLoaded && <ExperienceSuccess />}
			{!!itemFailureError && <ExperienceError error={itemFailureError} />}
		</>
	);
};

const PLACEHOLDER_CARD_SIZE_MAP = {
	[CardSize.SMALL]: SmallPlaceholderCard,
	[CardSize.MEDIUM]: MediumPlaceholderCard,
	[CardSize.LARGE]: LargePlaceholderCard,
};

const getPlaceholderCardComponent = (cardSize: CardSize) => {
	return PLACEHOLDER_CARD_SIZE_MAP[cardSize] ?? MediumPlaceholderCard;
};

const DynamicCardsContent = ({
	extensionLocalId,
	parameters,
	contentId,
	cardDimensions,
	CardComponent,
	shouldAutoscrollCards,
	isCompanyHub,
	isTargetAudience,
	isInViewMode = false,
	showDescription,
}: CardsContentProps) => {
	const {
		size,
		orientation,
		alignment,
		isPaddingEnabled,
		isAvatarShown,
		isPublishDateShown,
		isSocialStatsShown,
		isIconShown,
		filters,
		isSegmentationShown,
		audienceSegmentationGroupIds,
		segmentationTarget,
		renderVariant,
		backgroundColor,
		textColor,
		borderColor,
		cornerStyle,
		layout,
	} = parameters;
	const { cards, loading, error } = useCards(parameters);
	const { areAllItemsLoaded, itemFailureError, onItemSucceeded, onItemFailed } =
		useItemsCombinedExperienceState(cards);
	const { isAdminHubAIEnabled } = useProductDependencies();

	const { experienceSuccessComponent, experienceStopComponent } = useProductDependencies();
	const ExperienceSuccess = experienceSuccessComponent || (() => null);
	const ExperienceError = experienceStopComponent || (() => null);

	const numberOfCards = cards.length;

	const showAIDisclaimer =
		isAdminHubAIEnabled &&
		showDescription &&
		numberOfCards > 0 &&
		size !== CardSize.SMALL &&
		fg('company_hub_dynamic_cards_ai_descriptions');

	let cardsWithEmptyDefault = cards;
	if (!filters || !numberOfCards || loading) {
		cardsWithEmptyDefault = times<Card>(3, () => ({ cardId: '', link: '' }));
	}

	const { filteredCards } = useAudienceSegmentation({
		isCompanyHub,
		isSegmentationShown,
		isTargetAudience,
		cards: cardsWithEmptyDefault,
		isInViewMode,
		segmentationTarget,
		audienceSegmentationGroupIds,
		renderVariant,
	});

	const renderDynamicCard = useCallback(
		(card: Card) => {
			const isResultEmpty = !numberOfCards || !card?.link;

			// Cards 1.0 and 2.0 edge cases
			if (fg('company_hub_cards_visual_update')) {
				if (!filters) {
					return (
						<DynamicCardEmptyState
							cardDimensions={cardDimensions}
							cardSize={size}
							layout={layout}
							state={EmptyState.NO_FILTER}
						/>
					);
				}

				if (loading) {
					return (
						<DynamicCardLoadingState
							cardDimensions={cardDimensions}
							cardSize={size}
							layout={layout}
						/>
					);
				}

				if (isResultEmpty) {
					return (
						<DynamicCardEmptyState
							cardDimensions={cardDimensions}
							cardSize={size}
							layout={layout}
							state={EmptyState.NO_RESULTS}
						/>
					);
				}
			} else {
				if (!filters) {
					return <ZeroResultsCard cardDimensions={cardDimensions} cardSize={size} />;
				}

				if (loading) {
					const PlaceholderCardComponent = getPlaceholderCardComponent(size);
					return <PlaceholderCardComponent cardDimensions={cardDimensions} />;
				}

				if (isResultEmpty) {
					return <ZeroResultsCard cardDimensions={cardDimensions} cardSize={size} />;
				}
			}

			return (
				<Stack space="space.075" key={card.cardId} xcss={cx(styles.cardWrapper)}>
					<CardComponent
						contentId={contentId}
						cardDimensions={cardDimensions}
						isPaddingEnabled={isPaddingEnabled}
						isAvatarShown={isAvatarShown}
						isPublishDateShown={isPublishDateShown}
						isSocialStatsShown={isSocialStatsShown}
						isIconShown={isIconShown}
						isInViewMode={!!isInViewMode}
						onCardSucceeded={onItemSucceeded}
						onCardFailed={onItemFailed}
						backgroundColor={backgroundColor}
						textColor={textColor}
						borderColor={borderColor}
						cornerStyle={cornerStyle}
						enableSnippetRenderer={fg('company_hub_dynamic_cards_ai_descriptions')}
						showDescription={showDescription}
						{...card}
					/>
				</Stack>
			);
		},
		[
			isPaddingEnabled,
			isAvatarShown,
			isPublishDateShown,
			isSocialStatsShown,
			isIconShown,
			showDescription,
			filters,
			size,
			contentId,
			cardDimensions,
			isInViewMode,
			onItemSucceeded,
			onItemFailed,
			CardComponent,
			loading,
			backgroundColor,
			textColor,
			borderColor,
			cornerStyle,
			layout,
			numberOfCards,
		],
	);

	return (
		<>
			<CardsContainer
				shouldAutoscrollCards={shouldAutoscrollCards}
				numberOfCards={numberOfCards}
				cardDimensions={cardDimensions}
				isStacked={orientation === Orientation.STACKED}
				isInViewMode={isInViewMode}
				alignment={alignment}
				extensionLocalId={extensionLocalId}
				cards={filteredCards}
				renderCard={renderDynamicCard}
				showAIDisclaimer={showAIDisclaimer}
			/>
			{error && <LoadingCardsError error={error} />}
			{areAllItemsLoaded && <ExperienceSuccess />}
			{!!itemFailureError && <ExperienceError error={itemFailureError} />}
		</>
	);
};
